// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  MdPadding,
  Row,
  Toast,
  ToastContext,
} from '@alto/design-system';
import { type ShipmentTip } from '@alto/scriptdash/alto/orders/types/v1/shipment_tip';
import {
  type CreateShipmentTipRequest,
  type UpdateShipmentTipRequest,
} from '@alto/scriptdash/alto/patients/v3/courier_tips/wunderbar/v1/shipment_tips_endpoint';
import React, { useContext, useEffect, useState } from 'react';
import { DEFAULT_ERROR_ALERT_MESSAGE } from '~shared/constants';
import { SOURCES } from '~shared/features/courier-tips/AnalyticsProps';
import { COURIER_TIP_COPY, DEFAULT_TIP_AMOUNT } from '~shared/features/courier-tips/constants';
import { canSubmitCourierTip } from '~shared/features/courier-tips/helpers';
import { getShipmentTipForShipmentID } from '~shared/features/courier-tips/selectors';
import { getShipmentAddress } from '~shared/features/shipments/selectors/getShipments';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { formatDollars } from '~shared/helpers/currency';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { invalidateShipmentTips } from '~shared/queries/query-keys/shipmentTips';
import { useCreateShipmentTip, useUpdateShipmentTip } from '~shared/queries/useShipmentTips';
import { useSelectorShared } from '~shared/store';
import { EditOrAddTip } from '~web/features/courier-tips/components/EditOrAddTip';

type Props = {
  shipmentID: number;
};

export const TipShipmentActionSheet = ({ shipmentID }: Props) => {
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { mutateAsync: createShipmentTip } = useCreateShipmentTip();
  const { mutateAsync: updateShipmentTip } = useUpdateShipmentTip();

  const address = useSelectorShared((state) => getShipmentAddress(state, { shipmentID }));
  const shipmentTip = useSelectorShared((state) => getShipmentTipForShipmentID(state, { shipmentID }));

  const [tipAmount, setTipAmount] = useState(shipmentTip?.amount ?? DEFAULT_TIP_AMOUNT);
  const [tipPaymentMethodID, setTipPaymentMethodID] = useState(shipmentTip?.payment_method_id);

  const currentUserID = useSelectorShared(getCurrentUserID);
  const tipAnalyticsProps = {
    shipment_id: shipmentID,
    source: SOURCES.POST_DELIVERY,
    facility_id: address?.dispensing_facility_id,
  };

  useEffect(() => {
    void invalidateShipmentTips();
  }, []);

  const handleCourierTipError = (e: unknown) => {
    addToast(<Toast variant="error">{(e as Error)?.message || DEFAULT_ERROR_ALERT_MESSAGE}</Toast>);
    closeActionSheet();
  };

  const handleCourierTipSuccess = (tipAmount: number | null | undefined) => {
    if (typeof tipAmount === 'number' && tipAmount >= 0) {
      addToast(<Toast>{COURIER_TIP_COPY.TOAST_SUCCESSFUL_UPDATE}</Toast>);
    }
    closeActionSheet();
  };

  const handleSubmitCourierTip = async () => {
    if (!tipPaymentMethodID) return;

    trackEvent({
      event: EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_SUBMITTED,
      params: {
        ...tipAnalyticsProps,
        payment_method_id: tipPaymentMethodID,
        tip_value: formatDollars(tipAmount, false),
        view: undefined,
      },
    });
    if (shipmentTip) {
      const updatedShipmentTip: ShipmentTip = {
        ...shipmentTip,
        payment_method_id: tipPaymentMethodID,
        amount: tipAmount,
      };
      const request: UpdateShipmentTipRequest = {
        id: shipmentTip.id,
        shipment_tip: updatedShipmentTip,
      };

      try {
        const updatedTip = await updateShipmentTip(request);
        handleCourierTipSuccess(updatedTip?.amount);
      } catch (e) {
        handleCourierTipError(e);
      }
    } else if (currentUserID && shipmentID) {
      const request: CreateShipmentTipRequest = {
        user_id: currentUserID,
        shipment_id: shipmentID,
        payment_method_id: tipPaymentMethodID,
        amount: tipAmount,
      };
      try {
        const savedTip = await createShipmentTip(request);
        handleCourierTipSuccess(savedTip?.amount);
      } catch (e) {
        handleCourierTipError(e);
      }
    }
  };

  const handleDismissTip = () => {
    trackEvent({
      event: EVENTS.COURIER_TIPS__POST_DELIVERY_TIP_DISMISSED,
      params: {
        view: undefined,
        ...tipAnalyticsProps,
      },
    });
    closeActionSheet();
  };

  return (
    <ActionSheetV2 analyticsName="tip_shipment">
      <Column gap="MD">
        <EditOrAddTip
          editTipAmount={setTipAmount}
          editTipPaymentMethodID={setTipPaymentMethodID}
          tipAmount={tipAmount}
          tipPaymentMethodID={tipPaymentMethodID}
          analyticsProps={tipAnalyticsProps}
        />
        <MdPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
          leftPadding={SPACING.STATIC.NONE}
        >
          <Row
            right
            gap="SM"
          >
            <Button
              label="Dismiss"
              accessibilityLabel="Press to close modal"
              onPress={handleDismissTip}
              type="secondary"
            />
            <Button
              label="Submit"
              accessibilityLabel="Press to submit"
              disabled={!canSubmitCourierTip(tipAmount, tipPaymentMethodID)}
              onPress={handleSubmitCourierTip}
            />
          </Row>
        </MdPadding>
      </Column>
    </ActionSheetV2>
  );
};
